import { useRef, useState } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'

import type {
    ShopMedia,
    ShopMediaKind,
    ShopMediaUploadError,
} from '@/lib/api/shop-media-api'
import { Button } from '@/components/ui/button'
import { useUploadShopMedia } from '@/hooks/use-shop-media-query'
import { ApiError } from '@/lib/api/client'
import { MEDIA_ACCEPT } from '@/lib/api/shop-media-api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

/** Tối đa mỗi lần gửi (khớp server). */
export const MEDIA_UPLOAD_MAX = 20

function fileError(e: ShopMediaUploadError): string {
    return m.media_upload_file_error({
        name: e.filename,
        reason:
            resolveApiErrorMessage(e.code, e.params) ?? m.media_upload_failed(),
    })
}

/**
 * Tải nhiều file lên thư viện; báo số file thành công và lỗi riêng từng file
 * (file hỏng không chặn file khác). Trả các file đã vào thư viện.
 */
export function useMediaUploader(onUploaded?: (items: ShopMedia[]) => void) {
    const upload = useUploadShopMedia()
    const run = async (list: FileList | File[] | null | undefined) => {
        const files = Array.from(list ?? [])
        if (!files.length) return
        if (files.length > MEDIA_UPLOAD_MAX) {
            toast.error(m.media_upload_too_many({ max: MEDIA_UPLOAD_MAX }))
            return
        }
        try {
            const res = await upload.mutateAsync(files)
            const items = res.data?.items ?? []
            for (const e of res.data?.errors ?? []) toast.error(fileError(e))
            if (items.length) {
                toast.success(m.media_uploaded({ count: items.length }))
                onUploaded?.(items)
            }
        } catch (err) {
            toast.error(
                (err instanceof ApiError &&
                    resolveApiErrorMessage(err.code, err.params)) ||
                    m.media_upload_failed(),
            )
        }
    }
    return { run, pending: upload.isPending }
}

/** Nút "Tải lên" (chọn nhiều file từ máy). */
export function MediaUploadButton({
    kind,
    onUploaded,
    className,
}: {
    kind?: ShopMediaKind
    onUploaded?: (items: ShopMedia[]) => void
    className?: string
}) {
    const input = useRef<HTMLInputElement>(null)
    const { run, pending } = useMediaUploader(onUploaded)
    return (
        <>
            <input
                ref={input}
                type="file"
                multiple
                accept={MEDIA_ACCEPT[kind ?? 'all']}
                className="sr-only"
                aria-label={m.media_upload_input_aria()}
                tabIndex={-1}
                onChange={(e) => {
                    void run(e.target.files).finally(() => {
                        if (input.current) input.current.value = ''
                    })
                }}
            />
            <Button
                type="button"
                className={className}
                disabled={pending}
                onClick={() => input.current?.click()}
            >
                {pending ? (
                    <Loader2 className="size-4 animate-spin" />
                ) : (
                    <Upload className="size-4" />
                )}
                {m.media_upload()}
            </Button>
        </>
    )
}

/** Vùng kéo-thả file vào thư viện (bọc lưới ảnh). */
export function MediaDropZone({
    onUploaded,
    children,
    className,
}: {
    onUploaded?: (items: ShopMedia[]) => void
    children: React.ReactNode
    className?: string
}) {
    const [over, setOver] = useState(false)
    const { run } = useMediaUploader(onUploaded)
    return (
        <div
            className={cn(
                'relative rounded-lg transition-colors',
                over && 'bg-primary/5 ring-2 ring-dashed ring-primary',
                className,
            )}
            onDragOver={(e) => {
                if (!e.dataTransfer.types.includes('Files')) return
                e.preventDefault()
                setOver(true)
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
                if (!e.dataTransfer.files.length) return
                e.preventDefault()
                setOver(false)
                void run(e.dataTransfer.files)
            }}
        >
            {children}
            {over && (
                <p className="pointer-events-none absolute inset-x-0 top-4 text-center text-sm font-medium text-primary">
                    {m.media_drop_here()}
                </p>
            )}
        </div>
    )
}
